import { useState, useEffect } from "react";
import { Modal, Button, Form, Table, Alert, Badge, InputGroup, Spinner } from "react-bootstrap";
import { observer } from "mobx-react";

function UtmConfigModal({ show, onHide, deeplink, store }) {
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [name, setName] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [alert, setAlert] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [bulkDeleteMode, setBulkDeleteMode] = useState(new Set());

  useEffect(() => {
    if (show && deeplink) {
      // Load UTM configurations if not already loaded
      if (!deeplink.utmConfigurations) {
        store.fetchUtmConfigs(deeplink.path).catch((error) => {
          setAlert({ type: "danger", message: "Failed to load UTM configurations" });
        });
      }
    }
  }, [show, deeplink, store]);

  const handleCreateUtmConfig = async (e) => {
    e.preventDefault();

    if (!utmSource || !name) {
      setAlert({ type: "danger", message: "UTM Source and Name are required" });
      return;
    }

    try {
      const config = {
        utmSource,
        utmMedium: utmMedium || undefined,
        utmCampaign: utmCampaign || undefined,
        name,
      };

      await store.createUtmConfig(deeplink.path, config);

      // Reset form
      setUtmSource("");
      setUtmMedium("");
      setUtmCampaign("");
      setName("");
      setShowCreateForm(false);
      setAlert({ type: "success", message: "UTM configuration created successfully" });
    } catch (error) {
      setAlert({
        type: "danger",
        message: error.message || "Failed to create UTM configuration",
      });
    }
  };

  const handleDeleteUtmConfig = async (config, bulkDelete = false) => {
    const isBulkDelete = bulkDelete || bulkDeleteMode.has(config.id);

    const confirmMessage = isBulkDelete
      ? `Are you sure you want to delete ALL UTM configurations with source "${config.utmSource}"${
          config.utmMedium ? ` and medium "${config.utmMedium}"` : ""
        }${config.utmCampaign ? ` and campaign "${config.utmCampaign}"` : ""} across ALL deeplinks?\n\nThis will remove this configuration from ALL ${
          store.totalDeeplinks
        } deeplinks where it exists.`
      : `Are you sure you want to delete the UTM configuration "${config.name}" from this deeplink only?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      if (isBulkDelete) {
        // Clean up bulk delete mode BEFORE deletion (while configs still exist)
        const newBulkMode = new Set();
        bulkDeleteMode.forEach(configId => {
          const existingConfig = deeplink.utmConfigurations && deeplink.utmConfigurations.find(c => c.id === configId);
          if (existingConfig) {
            const sourceMatches = existingConfig.utmSource === config.utmSource;
            const mediumMatches = config.utmMedium 
              ? existingConfig.utmMedium === config.utmMedium
              : !existingConfig.utmMedium;
            const campaignMatches = config.utmCampaign
              ? existingConfig.utmCampaign === config.utmCampaign
              : !existingConfig.utmCampaign;
            if (!(sourceMatches && mediumMatches && campaignMatches)) {
              newBulkMode.add(configId);
            }
          }
        });
        
        const bulkConfig = {
          utmSource: config.utmSource,
          utmMedium: config.utmMedium || undefined,
          utmCampaign: config.utmCampaign || undefined,
        };
        const response = await store.deleteBulkUtmConfig(bulkConfig);
        setBulkDeleteMode(newBulkMode);
        setAlert({
          type: "success",
          message: `Successfully deleted ${response.deletedCount} UTM configurations across all deeplinks!`,
        });
      } else {
        await store.deleteUtmConfig(deeplink.path, config.id);
        setAlert({ type: "success", message: "UTM configuration deleted successfully" });
      }
    } catch (error) {
      setAlert({
        type: "danger",
        message: error.message || `Failed to delete UTM configuration${isBulkDelete ? "s" : ""}`,
      });
    }
  };

  const toggleBulkDeleteMode = (configId) => {
    const newBulkMode = new Set(bulkDeleteMode);
    if (newBulkMode.has(configId)) {
      newBulkMode.delete(configId);
    } else {
      newBulkMode.add(configId);
    }
    setBulkDeleteMode(newBulkMode);
  };

  const copyToClipboard = (text, configName) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedUrl(configName);
        setTimeout(() => setCopiedUrl(null), 2000);
      })
      .catch(() => {
        setAlert({ type: "danger", message: "Failed to copy to clipboard" });
      });
  };

  if (!deeplink) return null;

  const isLoadingConfigs = store.loadingUtmConfigs.has(deeplink.path);
  const isSavingConfig = store.savingUtmConfigs.has(deeplink.path);

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton className="bg-dark text-white">
        <Modal.Title>
          <span className="me-2">🔗</span>
          UTM Configurations for: <code className="text-warning">{deeplink.path}</code>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {alert && (
          <Alert variant={alert.type} onClose={() => setAlert(null)} dismissible className="mb-3">
            {alert.message}
          </Alert>
        )}

        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1">📊 Existing UTM Configurations</h5>
              <small className="text-muted">Use checkboxes to delete configurations from ALL deeplinks at once</small>
            </div>
            <Button variant="outline-dark" size="sm" onClick={() => setShowCreateForm(!showCreateForm)}>
              {showCreateForm ? "✖️ Cancel" : "➕ Add New"}
            </Button>
          </div>

          {isLoadingConfigs ? (
            <div className="text-center p-4">
              <Spinner animation="border" variant="secondary" className="mb-2" />
              <p className="text-muted">Loading configurations...</p>
            </div>
          ) : (
            <>
              {deeplink.utmConfigurations && deeplink.utmConfigurations.length > 0 ? (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Source</th>
                        <th>Medium</th>
                        <th>Campaign</th>
                        <th>URL</th>
                        <th>Delete Options</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {deeplink.utmConfigurations.map((config, index) => {
                        const isDeletingThis = store.deletingUtmConfigs.has(`${deeplink.path}-${config.id}`);
                        return (
                          <tr key={index}>
                            <td>
                              <strong>{config.name}</strong>
                            </td>
                            <td>
                              <Badge bg="primary">{config.utmSource}</Badge>
                            </td>
                            <td>
                              {config.utmMedium ? (
                                <Badge bg="secondary">{config.utmMedium}</Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              {config.utmCampaign ? (
                                <Badge bg="info">{config.utmCampaign}</Badge>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td style={{ maxWidth: "300px" }}>
                              <div className="d-flex align-items-center gap-2">
                                <code
                                  className="text-primary small text-truncate d-block"
                                  style={{ fontSize: "0.85rem" }}
                                  title={config.url}
                                >
                                  {config.url}
                                </code>
                                <Button
                                  variant={copiedUrl === config.name ? "success" : "outline-secondary"}
                                  size="sm"
                                  onClick={() => copyToClipboard(config.url, config.name)}
                                  className="flex-shrink-0"
                                >
                                  {copiedUrl === config.name ? "✅" : "📋"}
                                </Button>
                              </div>
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-2">
                                <Form.Check
                                  type="checkbox"
                                  id={`bulk-delete-${config.id}`}
                                  label="Delete from ALL deeplinks"
                                  checked={bulkDeleteMode.has(config.id)}
                                  onChange={() => toggleBulkDeleteMode(config.id)}
                                  disabled={isDeletingThis || store.deletingBulkUtm}
                                  className="small"
                                />
                                {bulkDeleteMode.has(config.id) && (
                                  <small className="text-warning">
                                    ⚠️ Will delete from ALL deeplinks with source: <strong>{config.utmSource}</strong>
                                    {config.utmMedium && (
                                      <span>
                                        {" "}
                                        & medium: <strong>{config.utmMedium}</strong>
                                      </span>
                                    )}
                                    {config.utmCampaign && (
                                      <span>
                                        {" "}
                                        & campaign: <strong>{config.utmCampaign}</strong>
                                      </span>
                                    )}
                                  </small>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex gap-2 flex-wrap">
                                <Button
                                  variant={bulkDeleteMode.has(config.id) ? "danger" : "outline-danger"}
                                  size="sm"
                                  onClick={() => handleDeleteUtmConfig(config)}
                                  disabled={isDeletingThis || store.deletingBulkUtm}
                                >
                                  {isDeletingThis || store.deletingBulkUtm ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : bulkDeleteMode.has(config.id) ? (
                                    "🌍 Delete All"
                                  ) : (
                                    "🗑️ Delete"
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center p-4 bg-light rounded">
                  <p className="text-muted mb-0">No UTM configurations yet. Create your first one!</p>
                </div>
              )}
            </>
          )}

          {/* Bulk Delete Summary */}
          {bulkDeleteMode.size > 0 && (
            <div className="mt-3 p-3 bg-warning bg-opacity-10 border border-warning rounded">
              <h6 className="text-warning mb-2">⚠️ Bulk Deletion Mode Active</h6>
              <p className="small mb-0">
                {bulkDeleteMode.size} configuration{bulkDeleteMode.size > 1 ? "s" : ""} selected for deletion across ALL
                deeplinks. These will be removed from all {store.totalDeeplinks} deeplinks when deleted.
              </p>
            </div>
          )}
        </div>

        {showCreateForm && (
          <div className="border-top pt-4">
            <h5 className="mb-3">➕ Create New UTM Configuration</h5>
            <Form onSubmit={handleCreateUtmConfig}>
              <Form.Group className="mb-3">
                <Form.Label>
                  Campaign Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., Facebook Campaign"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
                <Form.Text className="text-muted">A descriptive name for your campaign</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  UTM Source <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., facebook, google, newsletter"
                  value={utmSource}
                  onChange={(e) => setUtmSource(e.target.value)}
                  required
                />
                <Form.Text className="text-muted">The platform or referrer (required)</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  UTM Medium <span className="text-muted">(Optional)</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., social, cpc, email, stories"
                  value={utmMedium}
                  onChange={(e) => setUtmMedium(e.target.value)}
                />
                <Form.Text className="text-muted">The marketing medium (optional)</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  UTM Campaign <span className="text-muted">(Optional)</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., summer-sale, product-launch, holiday-promo"
                  value={utmCampaign}
                  onChange={(e) => setUtmCampaign(e.target.value)}
                />
                <Form.Text className="text-muted">The specific campaign name (optional)</Form.Text>
              </Form.Group>

              <div className="d-flex gap-2">
                <Button type="submit" variant="dark" disabled={isSavingConfig}>
                  {isSavingConfig ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Creating...
                    </>
                  ) : (
                    "✅ Create Configuration"
                  )}
                </Button>
                <Button variant="outline-secondary" onClick={() => setShowCreateForm(false)} disabled={isSavingConfig}>
                  Cancel
                </Button>
              </div>
            </Form>
          </div>
        )}

        <div className="mt-4 p-3 bg-light rounded">
          <h6 className="mb-2">📌 Base Deeplink URL:</h6>
          <div className="d-flex align-items-center gap-2">
            <code className="text-dark">{deeplink.url}</code>
            <Button variant="outline-secondary" size="sm" onClick={() => copyToClipboard(deeplink.url, "base")}>
              {copiedUrl === "base" ? "✅" : "📋"} Copy
            </Button>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default observer(UtmConfigModal);
